import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dropdown, Tabs } from "../../../shared";
import { useGetJobsQuery } from "../../../redux/services/jobs/jobsApi";
import { useNotifications } from "../../../contexts/NotificationContext";
import NotificationContent from "../../../layouts/Dashboard/NotificationsContent";
import { JobWorkspaceIcon } from "./icons";

/**
 * @param {object|undefined} result
 * @returns {Array<object>}
 */
function jobsFromResult(result) {
  if (Array.isArray(result?.jobs)) return result.jobs;
  if (Array.isArray(result?.docs)) return result.docs;
  return [];
}

/**
 * Top bar for the jobs v2 workspace: breadcrumb, search, and notifications.
 * @param {{ crumbs: Array<{ label: string, to?: string }> }} props
 * @returns {JSX.Element}
 */
export function WorkspaceBar({ crumbs }) {
  const navigate = useNavigate();
  const { stats } = useNotifications();
  const [searchOpen, setSearchOpen] = useState(false);
  const [term, setTerm] = useState("");
  const trimmed = term.trim();
  const { data } = useGetJobsQuery(
    { page: 1, limit: 6, search: true, searchTerm: trimmed },
    { skip: trimmed.length < 2 }
  );
  const matches = jobsFromResult(data);

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
        return;
      }
      if (event.key === "Escape" && searchOpen) {
        setSearchOpen(false);
        setTerm("");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [searchOpen]);

  const notifications = (close) => (
    <Tabs
      data={[
        {
          id: "Notifications",
          content: <NotificationContent onNavigate={close} />,
        },
      ]}
    />
  );

  return (
    <header className="flex h-[72px] items-center justify-between border-b border-[#e2e7ea] bg-white px-8">
      <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-3">
        {crumbs.map((crumb, index) => {
          const current = index === crumbs.length - 1;
          return (
            <React.Fragment key={`${crumb.label}-${index}`}>
              {index > 0 ? <JobWorkspaceIcon name="chevronRight12" /> : null}
              {crumb.to && !current ? (
                <Link
                  to={crumb.to}
                  className="text-[12px] font-normal leading-normal text-[#6e7b84] hover:text-[#202b33]"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span
                  className={`truncate text-[12px] leading-normal ${
                    current
                      ? "font-medium text-[#202b33]"
                      : "font-normal text-[#6e7b84]"
                  }`}
                >
                  {crumb.label}
                </span>
              )}
            </React.Fragment>
          );
        })}
      </nav>
      <div className="job-workspace-actions flex items-center gap-5">
        <div className="relative">
          {searchOpen ? (
            <label className="flex items-center gap-2">
              <JobWorkspaceIcon name="search" />
              <input
                autoFocus
                value={term}
                onChange={(event) => setTerm(event.target.value)}
                onBlur={() => {
                  if (!trimmed) setSearchOpen(false);
                }}
                placeholder="Search workspace"
                aria-label="Search workspace"
                className="w-[140px] border-0 bg-transparent p-0 text-[12px] font-normal text-[#202b33] outline-none placeholder:text-[#909ba3]"
              />
            </label>
          ) : (
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 text-left"
            >
              <JobWorkspaceIcon name="search" />
              <span className="text-[12px] font-normal leading-normal text-[#909ba3]">
                Search workspace
              </span>
              <span className="text-[10px] font-normal leading-normal text-[#909ba3]">
                ⌘ K
              </span>
            </button>
          )}
          {searchOpen && trimmed.length >= 2 ? (
            <ul className="absolute right-0 top-8 z-20 w-64 overflow-hidden rounded-[6px] border border-[#e2e7ea] bg-white shadow-sm">
              {matches.length === 0 ? (
                <li className="px-3 py-2 text-[12px] text-[#6e7b84]">No matching jobs</li>
              ) : (
                matches.map((job) => (
                  <li key={job._id}>
                    <button
                      type="button"
                      className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-[#f7f9fa]"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        setSearchOpen(false);
                        setTerm("");
                        navigate(`/jobs/v2/${job._id}`);
                      }}
                    >
                      <span className="text-[12px] font-semibold text-[#202b33]">
                        {job.jobId || job.loadNumber || "Job"}
                      </span>
                      <span className="text-[11px] text-[#6e7b84]">
                        {job.loadNumber || "Load not provided"}
                      </span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          ) : null}
        </div>
        <span className="h-5 w-px bg-[#e2e7ea]" />
        <Dropdown
          align="right"
          widthClasses="w-96"
          data={notifications}
          trigger={
            <button type="button" aria-label="Notifications" className="relative">
              <JobWorkspaceIcon name="bell" />
              {stats?.unread > 0 ? (
                <span className="absolute -right-1 -top-1 size-[6px] rounded-full bg-[#167366]" />
              ) : null}
            </button>
          }
        />
      </div>
    </header>
  );
}
